import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import VideoModal from './VideoModal'
import HomeTopForm from '@/components/ContactForm/HomeTopForm'

interface HeroProps {
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}

	background_desktop?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
	}
	background_mobile?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
	}
	button?: {
		href: string
		label: string
	}
	video?: {
		embed_url: string
	}
}

export const fields = [
	'heading.title',
	'text.text',

	'background_desktop.alt',
	'background_desktop.image_file.image.id',
	'background_desktop.image_file.image.filename_download',
	'background_mobile.alt',
	'background_mobile.image_file.image.id',
	'background_mobile.image_file.image.filename_download',

	'button.href',
	'button.label',

	'video.embed_url',
] as const

export default function Hero({ data }: { data: HeroProps }) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

  return (
		<>
			<section
				id='homepage-hero'
				className='HomeHero relative h-[calc(100vh-73px)] sm:h-[37.5rem] 2xl:h-[45rem]  bg-white overflow-hidden'
			>
				{/* Background Image */}
				<div className='absolute inset-0 w-full h-full'>
					<picture id='hero-image'>
						<source
							srcSet={getReadableImageUrl(
								data?.background_desktop?.image_file?.image?.id || '',
								data?.background_desktop?.image_file?.image
									?.filename_download || '',
							)}
							media='(min-width: 768px)'
							type='image/webp'
						/>
						<Image
							src={getReadableImageUrl(
								data?.background_mobile?.image_file?.image?.id || '',
								data?.background_mobile?.image_file?.image?.filename_download ||
									'',
							)}
							alt={data?.background_mobile?.alt || ''}
							fill
							className='object-cover object-top'
							priority
							quality={70}
							sizes='(max-width: 48rem) 100vw, (max-width: 80rem) 100vw, 100vw'
							fetchPriority='high'
						/>
					</picture>
				</div>

				{/* Gradient overlay */}
				<div className='absolute inset-0 bg-gradient-to-b from-transparent via-[#193762]/40 to-[#193762]/60 sm:bg-gradient-to-r sm:from-white/60 sm:[--tw-gradient-stops:var(--tw-gradient-from),rgb(255_255_255_/_0.1)_var(--tw-gradient-via-position),var(--tw-gradient-to)] sm:to-transparent' />

				{/* Content */}
				<div className='container h-full relative flex items-end sm:items-center pb-8 sm:pb-0'>
					<div className='flex flex-col'>
						<h1
							className='text-white text-4xl md:text-6xl md:text-[4rem] sm:text-black  md:leading-[5rem] font-agopus mb-6 sm:mb-8 md:mb-12 tracking-[0.5px]'
							dangerouslySetInnerHTML={sanitizeHTML(data?.heading?.title || '')}
						/>

						{/* Bullets */}
						<div
							className='flex flex-col gap-3 sm:gap-5 md:gap-6 mb-6 sm:mb-8 md:mb-12 max-md:text-white [&_ul]:max-md:text-white [&_ul_li::marker]:max-md:text-white'
							dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
						/>

						{/* Buttons */}
						<div className='flex flex-col-reverse sm:flex-row items-center gap-3 sm:gap-6'>
							<Link
								href={data?.button?.href || ''}
								className='w-full h-[60px] px-9 py-6 bg-[#9e6735] rounded-lg justify-center items-center gap-2 inline-flex overflow-hidden sm:w-auto sm:px-8 sm:py-3 md:py-4 sm:bg-[#B67D49] hover:opacity-90 transition-opacity'
							>
								<span className='text-center text-neutral-50 text-xl font-brandon leading-7 sm:text-xl md:text-2xl sm:leading-7 md:leading-9'>
									{data?.button?.label || ''}
								</span>
							</Link>

							<button
								className='group hidden sm:inline-flex items-center gap-3 w-full sm:w-auto justify-center px-6 py-3 rounded-lg border border-white sm:border-none sm:p-0'
								onClick={() => setIsVideoModalOpen(true)}
							>
								<span className='text-white sm:text-black text-base sm:text-xl font-brandon leading-6 sm:leading-7 md:leading-9'>
									Play Welcome Video
								</span>
								<div className='relative flex items-center justify-center w-8 h-8 sm:w-[35px] sm:h-[35px] border border-white sm:border-black rounded-full group-hover:bg-black/5 transition-colors'>
									<svg
										width='11'
										height='13'
										viewBox='0 0 11 13'
										fill='none'
										xmlns='http://www.w3.org/2000/svg'
										className='ml-0.5'
									>
										<path
											d='M0.838267 1.26122C0.838486 1.13003 0.873985 1.00131 0.941046 0.88856C1.00811 0.775807 1.10426 0.683167 1.21943 0.620347C1.3346 0.557528 1.46455 0.526841 1.59565 0.531504C1.72676 0.536166 1.8542 0.576004 1.96461 0.646846L10.1211 5.888C10.2244 5.95417 10.3094 6.04526 10.3682 6.15287C10.4271 6.26049 10.4579 6.38118 10.4579 6.50383C10.4579 6.62649 10.4271 6.74718 10.3682 6.8548C10.3094 6.96241 10.2244 7.05349 10.1211 7.11967L1.96461 12.3623C1.8542 12.4331 1.72676 12.473 1.59565 12.4776C1.46455 12.4823 1.3346 12.4516 1.21943 12.3888C1.10426 12.326 1.00811 12.2333 0.941046 12.1206C0.873985 12.0078 0.838486 11.8791 0.838267 11.7479V1.26122Z'
											fill='white'
											className='sm:fill-black'
										/>
									</svg>
								</div>
							</button>
						</div>
					</div>
				</div>
				<VideoModal
					isOpen={isVideoModalOpen}
					onClose={() => setIsVideoModalOpen(false)}
					videoUrl={data?.video?.embed_url}
				/>
			</section>
			<HomeTopForm />
		</>
	)
}